/**
 * HustleUpDiscoverPage — /up/groups
 *
 * Public, unauthenticated index page for HustleUp groups.
 * Filterable by category + region + keyword.  Powers SEO long-tail
 * landing pages (e.g. /up/groups?region=Perth&category=tech).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Users, MapPin, ArrowRight, Loader2, Mail, Check } from "lucide-react";
import "./HustleUpPublicGroupPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const CATS = [
  ["", "All categories"],
  ["outdoors", "Outdoors"],
  ["tech", "Tech & Code"],
  ["health", "Health & Wellness"],
  ["fitness", "Fitness"],
  ["food", "Food & Drink"],
  ["business", "Business & Career"],
  ["arts", "Arts & Crafts"],
  ["music", "Music"],
  ["parenting", "Parenting"],
  ["language", "Language"],
  ["pets", "Pets"],
  ["travel", "Travel"],
  ["volunteer", "Volunteer"],
];

export default function HustleUpDiscoverPage() {
  const [cat, setCat] = useState("");
  const [region, setRegion] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Digest subscription
  const [digestEmail, setDigestEmail] = useState("");
  const [digestRegion, setDigestRegion] = useState("");
  const [digestState, setDigestState] = useState({ busy: false, done: false, error: "" });

  useEffect(() => {
    document.title = "Discover HustleUp Groups · Free meetups near you";
    let el = document.head.querySelector('meta[name="description"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "description");
      document.head.appendChild(el);
    }
    el.setAttribute("content",
      "Browse free local groups across Australia — outdoors, tech, food, "
      + "arts, parenting and more. Free to join, free to host on HustleUp.");
  }, []);

  useEffect(() => {
    let dead = false;
    setLoading(true);
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (region) p.set("region", region);
    if (q) p.set("q", q);
    p.set("limit", "30");
    api.get(`/api/hustleup/public/groups?${p.toString()}`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ total: 0, results: [] }); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [cat, region, q]);

  const submitDigest = async (e) => {
    e?.preventDefault?.();
    setDigestState({ busy: true, done: false, error: "" });
    try {
      await api.post(`/api/hustleup/digest/subscribe`, {
        email: digestEmail.trim(),
        region: digestRegion.trim() || undefined,
        category: cat || undefined,
      });
      setDigestState({ busy: false, done: true, error: "" });
      setTimeout(() => setDigestState({ busy: false, done: false, error: "" }), 6000);
      setDigestEmail("");
    } catch (err) {
      setDigestState({ busy: false, done: false,
        error: err?.response?.data?.detail || "Could not subscribe — try again." });
    }
  };

  return (
    <div className="hupg-root" data-testid="hu-discover-root">
      <header className="hupg-topbar">
        <Link to="/hustleup" className="hupg-back">
          ← Hustle<span style={{ color: "#fbbf24" }}>Up</span>
        </Link>
        <div style={{ fontSize: 12, color: "rgba(251,191,36,0.7)" }}>
          {data ? `${data.total} groups` : ""}
        </div>
      </header>

      {/* Hero */}
      <section className="hupg-hero" style={{ minHeight: 220 }}>
        <div className="hupg-hero__inner">
          <span className="hupg-hero__cat">Discover</span>
          <h1 className="hupg-hero__title">
            Free local groups,<br/>across Australia.
          </h1>
          <p style={{ color: "rgba(255,255,255,0.78)", maxWidth: 560,
                       margin: "12px 0 0", fontSize: 15, lineHeight: 1.55 }}>
            From Perth photography walks to Cairns code-and-coffee.
            All free. No subscription. Hosted by locals.
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="hupd-filter-bar">
        <div className="hupd-filter-bar__inner">
          <div className="hupd-search">
            <Search size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search groups…"
              data-testid="hu-discover-search"
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)}
                  data-testid="hu-discover-cat" className="hupd-select">
            {CATS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Region (e.g. Perth, Brisbane)…"
            className="hupd-region"
            data-testid="hu-discover-region"
          />
        </div>
      </div>

      <main className="hupg-body" style={{ paddingTop: 28 }}>
        {/* Grid */}
        {loading ? (
          <div className="hupg-loading"><Loader2 className="hupg-spin"/> Loading…</div>
        ) : (data?.results || []).length === 0 ? (
          <p className="hupg-empty" data-testid="hu-discover-empty">
            No groups match — try a different search or region.
          </p>
        ) : (
          <div className="hupd-grid" data-testid="hu-discover-grid">
            {data.results.map((g) => (
              <Link
                key={g.slug}
                to={`/up/g/${g.slug}`}
                className="hupd-card"
                data-testid="hu-discover-card"
              >
                <div className="hupd-card__cat">{g.category}</div>
                <h3 className="hupd-card__name">{g.name}</h3>
                <div className="hupd-card__meta">
                  <span><MapPin size={11}/> {g.region}</span>
                  <span><Users size={11}/> {g.member_count}</span>
                </div>
                <p className="hupd-card__desc">{g.description}</p>
                <span className="hupd-card__cta">
                  View group <ArrowRight size={13}/>
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Weekly digest opt-in */}
        <section className="hupd-digest" data-testid="hu-digest-section">
          <div className="hupd-digest__copy">
            <div className="hupg-hero__cat" style={{ background: "rgba(251,191,36,0.18)", color: "#92400e" }}>
              <Mail size={11}/> Weekly digest
            </div>
            <h3>Get new groups + events near you in one weekly email.</h3>
            <p>One email, Monday mornings 9am AEST. Region-filtered.
              Unsub in one click. We never share your address.</p>
          </div>
          <form className="hupd-digest__form" onSubmit={submitDigest}>
            {digestState.done ? (
              <div className="hupd-digest__done" data-testid="hu-digest-done">
                <Check size={28}/>
                <div>You&apos;re on the list. Monday morning, 9am AEST.</div>
              </div>
            ) : (
              <>
                <input
                  type="email"
                  value={digestEmail}
                  onChange={(e) => setDigestEmail(e.target.value)}
                  placeholder="you@email.com"
                  required
                  data-testid="hu-digest-email"
                />
                <input
                  value={digestRegion}
                  onChange={(e) => setDigestRegion(e.target.value)}
                  placeholder="Region (optional) — e.g. Perth"
                  data-testid="hu-digest-region"
                />
                {digestState.error && (
                  <p className="hupd-digest__error">{digestState.error}</p>
                )}
                <button
                  type="submit"
                  disabled={digestState.busy}
                  data-testid="hu-digest-submit"
                >
                  {digestState.busy ? <Loader2 className="hupg-spin" size={14}/> : <Mail size={14}/>}
                  {digestState.busy ? "Subscribing…" : "Subscribe to weekly digest"}
                </button>
              </>
            )}
          </form>
        </section>
      </main>

      <footer className="hupg-footer">
        Powered by Hustle<span style={{ color: "#fbbf24" }}>Up</span>.au
        · Free to host. Free to attend.
      </footer>

      <style>{`
        .hupd-filter-bar {
          background: #fff; border-bottom: 1px solid #e8e2d4;
          position: sticky; top: 50px; z-index: 20;
        }
        .hupd-filter-bar__inner {
          max-width: 1100px; margin: 0 auto;
          padding: 14px 24px;
          display: grid;
          grid-template-columns: 2fr 1fr 1.2fr;
          gap: 10px;
        }
        @media (max-width: 680px) {
          .hupd-filter-bar__inner { grid-template-columns: 1fr; }
        }
        .hupd-search {
          display: flex; align-items: center; gap: 8px;
          background: #fffef5; border: 1px solid #e8e2d4;
          padding: 0 14px; border-radius: 10px;
        }
        .hupd-search input {
          border: 0; outline: 0; padding: 11px 0;
          font-size: 14px; background: transparent;
          width: 100%; font-family: inherit;
        }
        .hupd-select, .hupd-region {
          background: #fffef5;
          border: 1px solid #e8e2d4;
          border-radius: 10px;
          padding: 11px 14px;
          font-size: 14px; font-family: inherit;
          outline: 0;
        }
        .hupd-card {
          background: #fff;
          border: 1px solid #e8e2d4;
          border-radius: 14px;
          padding: 18px;
          text-decoration: none; color: inherit;
          display: flex; flex-direction: column;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .hupd-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 28px rgba(15,23,42,0.08);
        }
        .hupd-card__cat {
          font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.12em;
          color: #92400e; background: #fef3c7;
          padding: 3px 8px; border-radius: 4px;
          align-self: flex-start;
        }
        .hupd-card__name {
          font-family: Georgia, serif;
          font-size: 18px; font-weight: 800;
          letter-spacing: -0.015em;
          margin: 12px 0 6px;
        }
        .hupd-card__meta {
          display: flex; gap: 12px;
          font-size: 12px; color: #64748b;
          margin-bottom: 10px;
        }
        .hupd-card__meta span {
          display: inline-flex; align-items: center; gap: 4px;
        }
        .hupd-card__desc {
          font-size: 13px; color: #475569; line-height: 1.55;
          margin: 0 0 14px; flex-grow: 1;
        }
        .hupd-card__cta {
          font-size: 12px; font-weight: 700;
          color: #0f172a;
          display: inline-flex; align-items: center; gap: 4px;
        }

        /* Digest opt-in card */
        .hupd-digest {
          background: linear-gradient(180deg, #fffef5 0%, #fef3c7 100%);
          border: 1px solid #fde68a;
          border-radius: 16px;
          padding: 32px;
          margin-top: 48px;
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 32px;
          align-items: center;
        }
        @media (max-width: 760px) {
          .hupd-digest { grid-template-columns: 1fr; padding: 24px; }
        }
        .hupd-digest__copy h3 {
          font-family: Georgia, serif;
          font-size: 24px; font-weight: 800;
          letter-spacing: -0.02em;
          margin: 12px 0 6px;
        }
        .hupd-digest__copy p {
          font-size: 13px; color: #475569; line-height: 1.55; margin: 0;
        }
        .hupd-digest__form {
          display: flex; flex-direction: column; gap: 10px;
        }
        .hupd-digest__form input {
          padding: 12px 14px;
          border: 1px solid #fde68a;
          border-radius: 10px;
          font-size: 14px; font-family: inherit;
          background: #fff;
          outline: 0;
        }
        .hupd-digest__form input:focus { border-color: #f59e0b; }
        .hupd-digest__form button {
          background: #0f172a; color: #fbbf24;
          border: 0; padding: 13px 16px;
          border-radius: 10px;
          font-size: 14px; font-weight: 700; font-family: inherit;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          cursor: pointer;
        }
        .hupd-digest__form button:disabled { opacity: 0.7; cursor: wait; }
        .hupd-digest__error {
          margin: 0; font-size: 12px; color: #b91c1c;
        }
        .hupd-digest__done {
          background: #fff; border: 2px solid #10b981;
          padding: 18px; border-radius: 10px;
          text-align: center; color: #065f46; font-weight: 700;
          font-size: 14px;
        }
        .hupd-digest__done svg { color: #10b981; margin-bottom: 6px; }
      `}</style>
    </div>
  );
}
